/**
 * Sentry — frontend init + scoped chat-tool-card capture helper.
 *
 * Why this file exists
 * --------------------
 * Until now the FE has had zero observability. Wave-17's chunk-class
 * collapse, the camelCase/JSONField axios bug, and OwnerQuestionModal's
 * unknown-key crash all blew past the user without ever surfacing to
 * the team — each one needed a customer to file a ticket or for Leo to
 * notice a blank page in chrome-devtools-mcp. The dev BE already
 * writes to a Sentry project (DSN in SSM `dev-env`); this file points
 * the FE at the same project so a single org/project shows BE + FE
 * events side-by-side, filterable by `tags.component`.
 *
 * Config rules (per Stream G brief)
 * ---------------------------------
 *   - DSN comes from ``import.meta.env.VITE_SENTRY_DSN``. Hard-coding
 *     the DSN string anywhere in the repo is forbidden — buildspec-dev
 *     already pulls every ``VITE_*`` var out of the SSM ``dev-env``
 *     blob and feeds it to the ``frontend-build`` image, so adding the
 *     DSN there propagates to the deploy automatically.
 *   - When the env var is unset (local dev without Sentry, vitest,
 *     unit-test workers) ``initSentry`` is a silent no-op. Likewise
 *     ``captureChatRenderError`` short-circuits before touching the
 *     Sentry SDK — keeps tests deterministic without per-test mocks.
 *   - Tracing/perf is OFF for now. We want errors first; once the
 *     volume picture is clear we can enable
 *     ``browserTracingIntegration`` with a sampled rate.
 *
 * DRY across surfaces
 * -------------------
 * Per ``feedback_dry_across_surfaces``, a future BE Sentry breadcrumb
 * on the orchestrator side will reuse the SAME org/project and the
 * SAME ``component`` tag value (``chat-tool-card``) so both halves of a
 * failed render — backend tool-payload generation + frontend card
 * render — can be filtered together in the Sentry UI.
 */
import * as Sentry from "@sentry/react";

import { frontendBuild } from "@/lib/buildInfo";

/**
 * Initializes Sentry for the frontend. Must be called BEFORE any
 * component that could throw is mounted — i.e. at module-load time in
 * ``app.tsx``. Calling it after mount means the first error of the
 * session ships nowhere.
 *
 * Silent no-op when ``VITE_SENTRY_DSN`` is unset so local dev (and the
 * unit-test runner) doesn't need any special wiring.
 */
/** "tracker" for the /portal app, "other" for everything else. */
export const sentryAppFor = (path: string): "tracker" | "other" =>
  path === "/portal" || path.startsWith("/portal/") ? "tracker" : "other";

export const initSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Local dev / tests run without a DSN — that's the expected path.
    return;
  }
  Sentry.init({
    dsn,
    /*
     * This was ``import.meta.env.MODE``, with a comment claiming it split
     * FE-dev from FE-prod. It did not, and the comment was the opposite of
     * true. ``MODE`` is ``"production"`` for EVERY ``vite build`` — the dev
     * image and the prod image alike — and ``"development"`` only for a local
     * ``vite dev``. So every FE event this file has ever sent arrived tagged
     * ``environment: "production"``.
     *
     * WHAT THAT DID AND DID NOT COST, stated exactly, because the honest
     * version is narrower than it looks. Every one of those events came from
     * DEV, mislabelled as production. None came from prod: the prod frontend
     * bundle has never carried a Sentry DSN, so ``initSentry`` returns at the
     * guard above on prod and emits nothing. buildspec-dev.yml greps every
     * ``^VITE_`` key out of the SSM ``dev-env`` blob (DSN included);
     * buildspec-prod.yml greps only the two POSTHOG keys, and
     * frontend/Dockerfile.prod declares no ``VITE_SENTRY_DSN`` ARG at all.
     * So the defect was a dev channel labelling itself production, not two
     * channels colliding — and this fix does not make prod FE errors visible,
     * because prod FE reports nothing. Adding a prod DSN is a separate,
     * unmade decision (see the "TODO when moving to prod" notes below).
     *
     * ``VITE_BUILD_CHANNEL`` is the fact we actually want and it is now
     * plumbed end to end (buildspec -> /tmp/frontend.env ->
     * docker-compose-build-*.yml -> frontend/Dockerfile.* -> buildInfo.ts),
     * so this reports "dev" or "prod".
     *
     * ONE-TIME CONSEQUENCE on the next dev deploy: dev FE events move from
     * ``environment:production`` to ``environment:dev``. Any saved search,
     * alert rule, issue-owner rule or dashboard in that Sentry project scoped
     * to ``environment:production`` silently stops matching. Re-scope them to
     * ``dev`` — or to ``build.channel``, the tag below, which joins BE and FE.
     *
     * It degrades to the literal "unknown" (buildInfo's sentinel) rather than
     * falling back to MODE, deliberately: an unstamped bundle carrying a DSN
     * is a hand build, and "unknown" says so where "production" would lie
     * about it in the one field used to route alerts.
     */
    environment: frontendBuild.channel,
    release: import.meta.env.VITE_GIT_SHA || undefined,
    /*
     * Build identity as a tag, matching the backend's key and vocabulary
     * (server/build_stamp.py::sentry_tags). ONE tag, because everything else
     * about this bundle is already a first-class Sentry field: the sha is
     * ``release``, the channel is ``environment``.
     *
     * It is not redundant with ``environment`` even though it holds the same
     * string. The BACKEND's environment is its settings-module suffix
     * ("dev_settings" / "prod_settings"), so ``environment`` can never join
     * the two surfaces; ``build.channel:prod`` returns backend AND frontend
     * events from the same channel in one query. Same DRY-across-surfaces
     * rule the shared ``component`` tag below already follows.
     *
     * ``initialScope`` rather than a ``Sentry.setTag`` after init: it applies
     * to every event including any thrown between init and the first render.
     */
    initialScope: {
      tags: { "build.channel": frontendBuild.channel },
    },
    /*
     * Explicit off-switches for Sentry's paid features.
     *
     * These are explicitly 0 to prevent silent enablement of paid
     * features that would overlap PostHog and accidentally double-bill
     * us. PostHog (see ``src/analytics.ts``) owns session replay AND
     * the performance / web-vitals story for the FE; Sentry is errors-
     * only. Keep at 0 unless we're actively deciding to migrate
     * session replay or perf monitoring from PostHog to Sentry.
     *
     * TODO when moving to prod: keep these 0 in the prod buildspec /
     * prod Sentry project too unless Leo has explicitly signed off on
     * the PostHog→Sentry migration for that surface.
     */
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    integrations: [
      // Sentry.browserTracingIntegration({ ... })  // off until perf phase
    ],
    /**
     * Tags every event with the app it came from so Tracker errors can be
     * triaged first (Leo, 2026-10-05). Matches the backend ``app`` tag.
     */
    beforeSend(event) {
      event.tags = { ...event.tags, app: sentryAppFor(window.location.pathname) };
      return event;
    },
  });
};

/**
 * Captures a chat tool-card render failure with the standard tag/extra
 * shape the team has agreed on:
 *
 *   tags.component  = "chat-tool-card"
 *   tags.card_kind  = "<kind>"  // e.g. "list_active_violations"
 *   extra.payload   = <the offending payload>
 *
 * The ``component`` tag is shared with the BE so the future orchestrator-
 * side breadcrumb lands in the same Sentry filter.
 *
 * Short-circuits when ``VITE_SENTRY_DSN`` is unset — same as
 * ``initSentry``. Means the error-boundary fallback still renders in
 * local/test mode but no event ships and no SDK call fires (tests can
 * assert the no-op branch via ``vi.stubEnv``).
 */
export const captureChatRenderError = (
  error: unknown,
  payload: unknown,
  kind: string,
): void => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureException(error, {
    tags: { component: "chat-tool-card", card_kind: kind },
    extra: { payload },
  });
};
